import { z } from "zod";
import { ParticipantRole } from "./enums";
import { BBox } from "./geo";

// QR content (docs/05, ADR-0010). The QR encodes a universal link:
//   https://<domain>/j#t=<token>&p=<payload>&s=<signature>
// The token sits in the URL fragment so it never reaches server logs or Referer headers.
// The payload is signed by the server (Ed25519) so a phone can show the action name and start
// recording offline; the server still validates the token on the first contact.

export const InviteQrPayload = z.object({
  v: z.literal(1),
  /** action id */
  a: z.uuid(),
  /** action name, shown for confirmation before joining */
  n: z.string(),
  /** group name, if the invite is bound to a group */
  g: z.string().nullable(),
  r: ParticipantRole,
  /** HQ phone for SMS fallback */
  hq: z.string().nullable(),
  bb: BBox.nullable(),
  /** expiry, unix seconds */
  exp: z.number().int(),
  /** API base URL the phone should talk to */
  api: z.string(),
});
export type InviteQrPayload = z.infer<typeof InviteQrPayload>;

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function base64UrlDecode(text: string): Uint8Array {
  const padded = text.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

// UTF-8 without TextEncoder/TextDecoder: Hermes (React Native) lacks TextDecoder, so the same
// code must run in Node, browsers and on phones.
function utf8Encode(text: string): Uint8Array {
  const escaped = encodeURIComponent(text);
  const bytes: number[] = [];
  for (let i = 0; i < escaped.length; i++) {
    if (escaped[i] === "%") {
      bytes.push(Number.parseInt(escaped.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(escaped.charCodeAt(i));
    }
  }
  return Uint8Array.from(bytes);
}

function utf8Decode(bytes: Uint8Array): string {
  return decodeURIComponent(
    Array.from(bytes, (b) => `%${b.toString(16).padStart(2, "0")}`).join(""),
  );
}

/** Bytes that get signed: UTF-8 of the JSON exactly as embedded in the link. */
export function encodeInvitePayload(payload: InviteQrPayload): string {
  return base64UrlEncode(utf8Encode(JSON.stringify(payload)));
}

export function buildInviteLink(
  publicUrl: string,
  token: string,
  payload: string,
  signature: string,
): string {
  // Values are base64url, so they need no further escaping.
  return `${publicUrl.replace(/\/$/, "")}/j#t=${token}&p=${payload}&s=${signature}`;
}

export type ParsedInviteLink = {
  token: string;
  payloadEncoded: string;
  signature: string;
  payload: InviteQrPayload;
};

/** Parses a scanned link. Does not verify the signature (that needs the public key). */
export function parseInviteLink(link: string): ParsedInviteLink | null {
  const hashIndex = link.indexOf("#");
  if (hashIndex < 0) return null;
  // Parsed by hand: URLSearchParams in React Native is incomplete.
  const params = new Map(
    link
      .slice(hashIndex + 1)
      .split("&")
      .map((pair) => {
        const eq = pair.indexOf("=");
        return [pair.slice(0, eq), decodeURIComponent(pair.slice(eq + 1))] as const;
      }),
  );
  const token = params.get("t");
  const payloadEncoded = params.get("p");
  const signature = params.get("s");
  if (!token || !payloadEncoded || !signature) return null;
  try {
    const json = JSON.parse(utf8Decode(base64UrlDecode(payloadEncoded)));
    const payload = InviteQrPayload.safeParse(json);
    if (!payload.success) return null;
    return { token, payloadEncoded, signature, payload: payload.data };
  } catch {
    return null;
  }
}
